import { useState, useMemo } from "react";
import { FormFieldOption } from "@aam/shared";

interface CheckboxGroupFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: FormFieldOption[];
  gridCols?: number;
  required?: boolean;
  labelledBy?: string;
  describedBy?: string;
  invalid?: boolean;
  /** Optional pre-filter shown inline to the left of the search box. */
  categories?: Array<{ value: string; label: string }>;
  /** Whether an option belongs to the chosen category. Never called for "all". */
  matchesCategory?: (optionValue: string, category: string) => boolean;
  /** Options that cannot be picked, mapped to the reason shown on hover. */
  disabledOptions?: Record<string, string>;
}

export function CheckboxGroupField({
  value = [],
  onChange,
  options,
  gridCols = 3,
  labelledBy,
  describedBy,
  invalid,
  categories,
  matchesCategory,
  disabledOptions = {},
}: CheckboxGroupFieldProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(categories?.[0]?.value ?? "all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (q && !o.label.toLowerCase().includes(q)) return false;
      if (matchesCategory && category && category !== "all") {
        return matchesCategory(o.value, category);
      }
      return true;
    });
  }, [query, options, category, matchesCategory]);

  // Bulk actions and the "all selected" state ignore anything unavailable.
  const selectable = useMemo(
    () => filtered.filter((o) => !disabledOptions[o.value]),
    [filtered, disabledOptions]
  );

  const toggle = (optValue: string) => {
    if (disabledOptions[optValue]) return;
    onChange(
      value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue]
    );
  };

  const selectAll = () => onChange([...new Set([...value, ...selectable.map((o) => o.value)])]);
  const clearAll = () => onChange(value.filter((v) => !filtered.some((o) => o.value === v)));
  const allFilteredSelected =
    selectable.length > 0 && selectable.every((o) => value.includes(o.value));

  // Wide groups (states) now get the full section width, so they can fan out
  // further before the list needs its own scrollbar.
  const colClass =
    gridCols === 4
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
      : "grid-cols-2 sm:grid-cols-3";

  return (
    <div className="space-y-3" role="group" aria-labelledby={labelledBy} aria-describedby={describedBy} aria-invalid={invalid}>
      {/* Search + controls row */}
      <div className="flex items-center gap-2">
        {categories && categories.length > 0 && (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Filter by product type"
            className="shrink-0 min-h-11 rounded-lg border border-border-default bg-surface-input px-3 py-2 text-sm text-content-primary focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-all"
          >
            {categories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-muted pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            className="w-full pl-9 pr-11 py-2 rounded-lg bg-surface-input border border-border-default text-sm text-content-primary placeholder:text-content-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/20 transition-all"
            aria-label="Filter options"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-0 top-1/2 min-h-11 min-w-11 -translate-y-1/2 text-content-muted hover:text-content-primary transition-colors"
              aria-label="Clear option filter"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={allFilteredSelected ? clearAll : selectAll}
          className="shrink-0 min-h-11 px-3 py-2 rounded-lg text-xs font-medium bg-surface-muted hover:bg-surface-overlay text-content-primary transition-colors"
        >
          {allFilteredSelected ? "Deselect all" : "Select all"}
        </button>
      </div>

      {/* Selected count — always reserves space to avoid layout jump */}
      <p className="text-xs font-medium h-4">
        {value.length > 0 && (
          <span className="text-accent">{value.length} selected</span>
        )}
      </p>

      {/* Grid */}
      <div className={`grid ${colClass} gap-1.5 max-h-[26rem] overflow-y-auto pr-1`}>
        {filtered.map((option) => {
          const checked = value.includes(option.value);
          const unavailableReason = disabledOptions[option.value];
          return (
            <label
              key={option.value}
              data-testid={`option-${option.value}`}
              title={unavailableReason}
              aria-disabled={unavailableReason ? true : undefined}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg select-none transition-all text-sm ${
                unavailableReason
                  ? "cursor-not-allowed border border-border-subtle bg-surface-overlay/40 text-content-muted opacity-60"
                  : checked
                    ? "cursor-pointer bg-accent-muted border border-accent text-content-primary"
                    : "cursor-pointer bg-surface-overlay border border-border-default text-content-secondary hover:border-accent hover:text-content-primary"
              }`}
            >
              <input
                type="checkbox"
                value={option.value}
                checked={checked}
                disabled={Boolean(unavailableReason)}
                onChange={() => toggle(option.value)}
                className="peer sr-only"
              />
              <div
                className={`w-4 h-4 shrink-0 rounded border-2 flex items-center justify-center transition-all peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[var(--focus-ring)] ${
                  checked ? "bg-accent-strong border-accent" : "border-border-default"
                }`}
              >
                {checked && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="truncate leading-tight">{option.label}</span>
              {unavailableReason && (
                <span className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                  {unavailableReason}
                </span>
              )}
            </label>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-content-muted py-4 text-center">
          {query ? `No results for "${query}"` : "No options match this filter"}
        </p>
      )}
    </div>
  );
}
